/**
 * Map-only block for warehouse → delivery address.
 * Shown for LCL seafreight or airfreight when delivery type is "delivery" and user has entered an address.
 * No cost or kostenoverzicht addition — purely for showing the route map.
 */
import React, { useState, useEffect } from "react";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import { fetchRouteMapBlob } from "../../utils/staticMapUtils";
import { WAREHOUSE_COORDINATES } from "../../config/warehouse";
import { WAREHOUSE_LABEL } from "../../hooks/useWarehouseToAddressRoute";

const { Text } = Typography;

export interface WarehouseToAddressMapProps {
  destLabel: string | null;
  distanceKm: number | null;
  destCoords: { lat: number; lon: number } | null;
  routeGeometry: number[][];
  loading: boolean;
  error: string | null;
  /** Override default "LCL levering route" heading */
  title?: string;
  /** Compact map for side panels */
  compact?: boolean;
}


export const WarehouseToAddressMap: React.FC<WarehouseToAddressMapProps> = ({
  destLabel,
  distanceKm,
  destCoords,
  routeGeometry,
  loading: routeLoading,
  error,
  title,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [mapBlobUrl, setMapBlobUrl] = useState<string | null>(null);
  const [mapLoading, setMapLoading] = useState(false);

  const hasCoords = destCoords != null;
  const hasRoute = routeGeometry.length >= 2;
  const shouldFetchMap = hasCoords && distanceKm != null && !routeLoading && !error;

  const mapKey = shouldFetchMap
    ? `${WAREHOUSE_COORDINATES.lat},${WAREHOUSE_COORDINATES.lon}-${destCoords!.lat},${destCoords!.lon}-${hasRoute ? routeGeometry.length : 0}`
    : "";

  useEffect(() => {
    if (!shouldFetchMap) {
      setMapBlobUrl(null);
      setMapLoading(false);
      return;
    }

    let cancelled = false;
    setMapLoading(true);

    fetchRouteMapBlob(
      WAREHOUSE_COORDINATES,
      destCoords!,
      hasRoute ? routeGeometry : undefined,
    )
      .then((blobUrl) => {
        if (!cancelled && blobUrl) {
          setMapBlobUrl(blobUrl);
        }
      })
      .finally(() => {
        if (!cancelled) setMapLoading(false);
      });

    return () => {
      cancelled = true;
      setMapBlobUrl((prev) => {
        if (prev && !prev.startsWith("/")) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [mapKey, shouldFetchMap]);

  const heading =
    title ?? t("tools.calculator.lclDeliveryRoute", "LCL levering route");
  const mapMaxWidth = compact ? 320 : 400;
  const mapMinHeight = compact ? 140 : 180;

  if (error) {
    return (
      <div
        style={{
          marginTop: 8,
          padding: 8,
          background: "#fafafa",
          borderRadius: 4,
          border: "1px solid #f0f0f0",
        }}
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t("tools.calculator.mapUnavailable", "Kaart niet beschikbaar")}
        </Text>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: compact ? 0 : 8,
        padding: 8,
        background: "#fafafa",
        borderRadius: 4,
        border: "1px solid #f0f0f0",
      }}
    >
      <div style={{ marginBottom: 6 }}>
        <Text strong style={{ fontSize: 14 }}>
          {heading}
        </Text>
      </div>

      {WAREHOUSE_LABEL && destLabel && (
        <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
          {t("tools.calculator.route", "Route")}: {WAREHOUSE_LABEL} → {destLabel}
        </Text>
      )}

      {distanceKm != null && (
        <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
          {t("tools.calculator.distance", "Afstand")}: {distanceKm.toFixed(0)} km
        </Text>
      )}

      {shouldFetchMap && (
        <div
          style={{
            marginTop: 8,
            width: "100%",
            maxWidth: mapMaxWidth,
            aspectRatio: "4/3",
            minHeight: mapMinHeight,
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid #e8e8e8",
          }}
        >
          {(mapLoading || routeLoading) && (
            <div
              style={{
                width: "100%",
                height: "100%",
                minHeight: mapMinHeight,
                background: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                color: "#999",
              }}
            >
              {t("tools.calculator.loadingMap", "Kaart laden...")}
            </div>
          )}
          {mapBlobUrl && !mapLoading && !routeLoading && (
            <img
              src={mapBlobUrl}
              alt={t("tools.calculator.routeMap", "Route map")}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
          {!mapLoading && !routeLoading && !mapBlobUrl && (
            <div
              style={{
                width: "100%",
                height: "100%",
                minHeight: mapMinHeight,
                background: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                color: "#999",
              }}
            >
              {t("tools.calculator.mapUnavailable", "Kaart niet beschikbaar")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
